import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router';
import useScrollToTopOnPageChange from '../../../hooks/scroll/useScrollToTopOnPageChange';
import {Divider, Grid, Typography} from '@mui/material';
import LocationImageGallery from '../../../components/location/profile/LocationImageGallery';
import LocationProfileDescription from '../../../components/location/profile/LocationProfileDescription';
import LocationProfileMapPreview from '../../../components/location/profile/LocationProfileMapPreview';
import LocationProfileFeatures from '../../../components/location/profile/features/LocationProfileFeatures';
import LocationProfilePriceBreakdown from '../../../components/location/profile/LocationProfilePriceBreakdown';
import LocationProfileBookSpaceForm from '../../../components/location/profile/LocationProfileBookSpaceForm';
import LocationProfileBeforeYouBook from '../../../components/location/profile/LocationProfileBeforeYouBook';
import LocationProfileSimilarYardsNearby from '../../../components/location/profile/LocationProfileSimilarYardsNearby';
import LocationProfileHeader from '../../../components/location/profile/LocationProfileHeader';
import {
  getLocationByIdWithDates,
  getLocationHasRequestedCapacityById
} from '../../../components/location/requests/location-requests';
import moment from 'moment';
import SecurSpaceContainer from '../../../components/common/SecurSpaceContainer';
import './style.css'
import {DateFormats} from '../../../components/constants/securspace-constants';
import {DAILY, MONTHLY} from "../../../util/BookingUtil";
import {DEFAULT_DAILY_BOOKING_DAYS_AHEAD, DEFAULT_NUM_SPACES} from "../../../components/constants/book-space-constants";
import {withSnackbar} from "../../../components/hocs/withSnackbar";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../../util/NetworkErrorUtil";
import OverageRatePrice from "../../../components/location/profile/OverageRatePrice";
import {AppContext} from "../../../context/app-context";
import {isSupplierAccount} from "../../../util/AccountUtils";
import LocationProfileSEO from "../../../components/location/profile/LocationProfileSEO";
import {styled} from "@mui/material/styles";
import {usePrerenderReady} from "../../../hooks/usePrerenderReady";

const LpMapText = styled(Typography)(() => ({
  fontSize: '.8571428em',
  lineHeight: '166%',
  marginTop: '1.2em',
}));

const LpHeaderDivider = styled(Divider)(() => ({
  height: '0.07rem',
  color: '#e0e0e09d',
}));

const LpSectionDivider = styled(Divider)(() => ({
  height: '0.08rem',
  marginTop: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#e0e0e09d',
}));

const LpBody = styled(Grid)(() => ({
  marginTop: '1.5rem',
}));

const LpBeforeYouBookContainer = styled(Grid)(() => ({
  marginTop: '2.14rem',
}));

const LpMainBg = styled('section')(() => ({
  backgroundColor: '#fff'
}));

const LpListingInfoSection = styled(Grid)(({theme}) => ({
  rowGap: '1.07rem',
  [theme.breakpoints.between('md', 'lg')]: {
    width: '57.14rem',
  }
}));

const LpDivider = styled(Grid)(() => ({
  borderBottom: `1px solid #e0e0e09d`,
  paddingBottom: '0.1em'
}));

const LocationProfile = ({ snackbarShowMessage }) => {
  const { locationId } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  // True once the location fetch has settled (success or failure), so the
  // prerender snapshot waits for the real title/JSON-LD but is never held
  // hostage by a 404.
  const [locationFetched, setLocationFetched] = useState(false);
  const [capacityRequest, setCapacityRequest] = useState({
    durationType: DAILY,
    startDate: moment().format(DateFormats.DAY),
    endDate: moment().add(DEFAULT_DAILY_BOOKING_DAYS_AHEAD, 'days').format(DateFormats.DAY),
    numberOfSpaces: DEFAULT_NUM_SPACES,
  });
  const [locationHasRequestedCapacity, setLocationHasRequestedCapacity] = useState(null);
  const [minimumSpacesUpdated, setMinimumSpacesUpdated] = useState(false);
  const [isBrokeredBooking, setIsBrokeredBooking] = useState(false);
  const initialCapacityLoaded = useRef(false);

  const { user }  = useContext(AppContext);

  useScrollToTopOnPageChange();
  usePrerenderReady(locationFetched);

  useEffect(() => {
    // A location change while a fetch is in flight must not let the OLD
    // response set state (or flag the page ready) for the NEW id.
    let stale = false;
    if (locationId) {
      setLoading(true)
      setLocationFetched(false);

      const startDate = moment().format(DateFormats.DAY);
      const endDate = moment().add(1, 'days').format(DateFormats.DAY);

      // Check if URL params indicate a monthly deep-link. When they do, the daily
      // capacity response would be stale — skip seeding it and let the form's
      // monthly capacity check provide the correct value.
      const urlShowMonth = new URLSearchParams(window.location.search).get('showMonth');
      const isMonthlyDeepLink = urlShowMonth === 'true';

      getLocationByIdWithDates(locationId, startDate, endDate).then((response) => {
        if (stale) return;
        setLocation(response.body);
        setMinimumSpacesUpdated(true);
        if (!isMonthlyDeepLink) {
          setLocationHasRequestedCapacity(response.body.hasInstantApproval);
        } else {
          // Pre-arm the guard so the capacity-check effect does NOT skip its
          // first run; the daily value would be stale for a monthly deep-link.
          initialCapacityLoaded.current = true;
        }
      }).catch((error) => {
        if (stale) return;
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error', 15000);
      }).finally(() => {
        if (stale) return;
        setLoading(false);
        setLocationFetched(true);
      });
    }
    return () => { stale = true; };
  }, [locationId, snackbarShowMessage]);

  useEffect(() => {
    let ignore = false;
    const {numberOfSpaces, durationType} = capacityRequest;

    // These conditions are to limit the number of unnecessary calls to this endpoint
    if(minimumSpacesUpdated) {
      // Skip the first run after initial load — the API value is already set
      if (!initialCapacityLoaded.current) {
        initialCapacityLoaded.current = true;
        return;
      }
      // Monthly bookings always go through Request Space — no capacity check needed
      if (durationType === MONTHLY) {
        setLocationHasRequestedCapacity(false);
      } else if (isBrokeredBooking || numberOfSpaces >= location?.minNumberOfSpaces) {
        getLocationHasRequestedCapacityById(
          locationId,
          numberOfSpaces,
          capacityRequest.startDate,
          capacityRequest.endDate,
          durationType
        ).then((response) => {
          if (!ignore) {
            setLocationHasRequestedCapacity(response.body.hasRequestedCapacity);
          }
        }).catch(error => {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error', 15000);
        });
      } else {
        setLocationHasRequestedCapacity(false);
      }
    }
    return () => {
      ignore = true;
    }
  }, [locationId, capacityRequest, minimumSpacesUpdated, isBrokeredBooking, snackbarShowMessage, location]);

  const handleCapacityRequestChange = useCallback(function handleCapacityRequestChange(value) {
    setCapacityRequest(capacityRequest => ({
      ...capacityRequest,
      ...value,
    }));
  }, []);

  const locationGallery = useMemo(() => {
    if (!location) {
      return [];
    }
    return location.gallery ? [...new Set(location.gallery)] : [];
  }, [location]);

  return (
    <>
      <LocationProfileSEO location={location} locationId={locationId} />
      <LpMainBg>
        <SecurSpaceContainer >
          <Grid container direction={'column'}>
            <LpDivider item container xs>
              <LocationProfileHeader
                locationId={locationId}
                locationName={location?.locationName}
                city={location?.city}
                state={location?.state}
                hasRequestedCapacity={locationHasRequestedCapacity}
              />
            </LpDivider>
            <LpHeaderDivider variant='fullWidth' />
            <LpBody container spacing={4}>
              <LpListingInfoSection item xs={12} md>
                <Grid item>
                  <LocationImageGallery locationId={locationId} listingImage={location?.listingImageFileName} gallery={locationGallery} />
                </Grid>
                <Grid item>
                  <LocationProfileDescription
                    loading={loading}
                    desc={location?.locationDescription}
                    supplierProfileUrl={location?.supplierProfileUrl}
                    supplierName={location?.companyName}
                  />
                </Grid>
                <Grid item>
                  <LocationProfileMapPreview loading={loading} lat={location?.addressLatitude} lng={location?.addressLongitude} />
                </Grid>
                <Grid item>
                  <LpMapText>
                    For Security Reasons, addresses will not be sent before a booking is completed.
                    You will receive facility instructions and address upon completing.
                    This map represents the zip code of the facility.
                  </LpMapText>
                </Grid>
                <LpSectionDivider variant='fullWidth' />
                <Grid item>
                  <LocationProfileFeatures loading={loading} features={location?.features ? location.features : []} />
                </Grid>
                <LpSectionDivider variant='fullWidth' />
                <Grid item>
                  <LocationProfilePriceBreakdown
                    equipmentTypeArray={location?.equipmentTypesAll ? location.equipmentTypesAll : []}
                    defaultPricePerDay={location?.pricePerDay}
                    defaultPricePerMonth={location?.pricePerMonth}
                    loading={loading}
                  />
                </Grid>
                {location?.chargeOverages && <LpSectionDivider variant='fullWidth' />}
                {
                  location?.chargeOverages && <Grid item>
                    <OverageRatePrice overageRateInCents={location?.overageRate}/>
                  </Grid>
                }
              </LpListingInfoSection>
              {
                !isSupplierAccount(user) && <Grid item xs={12} md={4}>
                  <Grid container direction={'column'}>
                    <Grid item>
                      <LocationProfileBookSpaceForm
                        location={location}
                        capacityRequest={capacityRequest}
                        onCapacityRequestChange={handleCapacityRequestChange}
                        instantApproval={locationHasRequestedCapacity}
                        isBrokeredBooking={isBrokeredBooking}
                        setIsBrokeredBooking={setIsBrokeredBooking}
                        loading={loading}
                      />
                    </Grid>
                    <LpBeforeYouBookContainer item>
                      <LocationProfileBeforeYouBook rentalAgreementFileName={location?.supplierLegalAgreementFileName}/>
                    </LpBeforeYouBookContainer>
                  </Grid>
              </Grid>
              }
            </LpBody>
          </Grid>
        </SecurSpaceContainer>
      </LpMainBg>
      {
        location && <SecurSpaceContainer>
          <Grid container item xs>
            <LocationProfileSimilarYardsNearby location={location} />
          </Grid>
        </SecurSpaceContainer>
      }
    </>
  )
}

export default withSnackbar(LocationProfile);
